import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowSquareOutIcon,
  BellSlashIcon,
  CheckIcon,
  MagnifyingGlassIcon,
  XIcon,
} from '@phosphor-icons/react';
import { ALERT_STATUSES, SEVERITIES } from '@meshalert/contract';
import { api } from '../lib/api.ts';
import {
  DELIVERY_STATUS_LABEL,
  SEVERITY_LABEL,
  STATUS_LABEL,
  absoluteTime,
  duration,
  relativeTime,
} from '../lib/format.ts';
import { PageHeader } from '../components/Layout.tsx';
import { useSession } from '../lib/session.tsx';
import {
  Button,
  EmptyState,
  ErrorState,
  Input,
  ListSkeleton,
  Panel,
  Select,
  SeverityChip,
  StatusChip,
  Tag,
  cx,
  severityRuleClass,
} from '../components/ui.tsx';

export function AlertsPage() {
  const { alertId } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const [severity, setSeverity] = useState('');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [cursor, setCursor] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search), 250);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => setCursor(null), [status, severity, debounced]);

  const alerts = useQuery({
    queryKey: ['alerts', { status, severity, q: debounced, cursor }],
    queryFn: () =>
      api.alerts.list({
        limit: 25,
        cursor,
        ...(status ? { status } : {}),
        ...(severity ? { severity } : {}),
        ...(debounced ? { q: debounced } : {}),
      }),
    refetchInterval: 15_000,
  });

  const filtered = status !== '' || severity !== '' || debounced !== '';

  return (
    <>
      <PageHeader
        title="Alarme"
        description="Jeder eingegangene Alarm, zusammengefasst nach Bedingung. Wiederholungen zählen hoch, statt die Liste zu fluten."
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative min-w-[15rem] flex-1">
          <MagnifyingGlassIcon
            size={15}
            className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-ink-3"
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Titel oder Beschreibung durchsuchen"
            aria-label="Alarme durchsuchen"
            className="pl-8"
          />
        </div>
        <Select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          aria-label="Nach Status filtern"
          className="w-40"
        >
          <option value="">Jeder Status</option>
          {ALERT_STATUSES.map((s) => (
            <option key={s} value={s}>
              {STATUS_LABEL[s]}
            </option>
          ))}
        </Select>
        <Select
          value={severity}
          onChange={(e) => setSeverity(e.target.value)}
          aria-label="Nach Schweregrad filtern"
          className="w-40"
        >
          <option value="">Jeder Schweregrad</option>
          {[...SEVERITIES].reverse().map((s) => (
            <option key={s} value={s}>
              {SEVERITY_LABEL[s]}
            </option>
          ))}
        </Select>
      </div>

      <Panel bodyClassName="p-0">
        {alerts.isLoading ? (
          <ListSkeleton rows={8} />
        ) : alerts.isError ? (
          <ErrorState message="Die Alarme konnten nicht geladen werden." onRetry={() => alerts.refetch()} />
        ) : alerts.data && alerts.data.items.length > 0 ? (
          <>
            <ul className="divide-y divide-[var(--border-subtle)]">
              {alerts.data.items.map((alert) => (
                <li key={alert.id}>
                  <button
                    type="button"
                    onClick={() => navigate(`/alarme/${alert.id}`)}
                    className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-hover"
                  >
                    <span
                      aria-hidden
                      className={cx(
                        'h-9 w-[3px] shrink-0 rounded-full',
                        alert.status === 'resolved' ? 'bg-line-strong' : severityRuleClass(alert.severity),
                      )}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        <span
                          className={cx(
                            'truncate text-[13px] font-medium',
                            alert.status === 'resolved' ? 'text-ink-2' : 'text-ink',
                          )}
                        >
                          {alert.title}
                        </span>
                        {alert.occurrences > 1 ? (
                          <span className="tnum shrink-0 rounded-sm bg-sunken px-1 text-[11px] text-ink-3">
                            {alert.occurrences}x
                          </span>
                        ) : null}
                      </span>
                      <span className="mt-0.5 flex items-center gap-2 text-[12px] text-ink-3">
                        <span className="truncate">{alert.source_name}</span>
                        {alert.delivery_summary.dead > 0 ? (
                          <span className="shrink-0 text-critical">
                            {alert.delivery_summary.dead} Zustellung fehlgeschlagen
                          </span>
                        ) : null}
                      </span>
                    </span>
                    <span className="hidden shrink-0 sm:block">
                      <SeverityChip severity={alert.severity} />
                    </span>
                    <span className="hidden shrink-0 md:block">
                      <StatusChip status={alert.status} />
                    </span>
                    <span className="w-24 shrink-0 text-right text-[12px] text-ink-3">
                      {relativeTime(alert.last_seen_at)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>

            {alerts.data.next_cursor || cursor ? (
              <div className="flex items-center justify-between gap-3 border-t border-line px-4 py-3">
                <Button size="sm" disabled={!cursor} onClick={() => setCursor(null)}>
                  Zurück zum Anfang
                </Button>
                <Button
                  size="sm"
                  disabled={!alerts.data.next_cursor}
                  onClick={() => setCursor(alerts.data.next_cursor)}
                >
                  Weitere laden
                </Button>
              </div>
            ) : null}
          </>
        ) : filtered ? (
          <EmptyState
            title="Kein Treffer"
            description="Mit diesen Filtern gibt es keinen Alarm. Setz den Suchbegriff oder die Auswahl zurück."
            action={
              <Button
                size="sm"
                onClick={() => {
                  setSearch('');
                  setStatus('');
                  setSeverity('');
                }}
              >
                Filter zurücksetzen
              </Button>
            }
          />
        ) : (
          <EmptyState
            icon={<BellSlashIcon size={26} weight="light" />}
            title="Noch kein Alarm eingegangen"
            description="Sobald eine Quelle etwas an den Ingest-Endpunkt schickt, erscheint es hier. Eine Quelle mit Token legst du unter Quellen an."
          />
        )}
      </Panel>

      {alertId ? <AlertDetail alertId={alertId} onClose={() => navigate('/alarme')} /> : null}
    </>
  );
}

function AlertDetail({ alertId, onClose }: { alertId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { can } = useSession();
  const mayAct = can('admin', 'operator');

  const alert = useQuery({
    queryKey: ['alert', alertId],
    queryFn: () => api.alerts.get(alertId),
    refetchInterval: 15_000,
  });

  const deliveries = useQuery({
    queryKey: ['deliveries', alertId],
    queryFn: () => api.deliveries.list({ alert_id: alertId, limit: 50 }),
    refetchInterval: 15_000,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['alert', alertId] });
    void queryClient.invalidateQueries({ queryKey: ['alerts'] });
    void queryClient.invalidateQueries({ queryKey: ['stats'] });
  };

  const acknowledge = useMutation({ mutationFn: () => api.alerts.acknowledge(alertId), onSuccess: invalidate });
  const resolve = useMutation({ mutationFn: () => api.alerts.resolve(alertId), onSuccess: invalidate });

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const data = alert.data;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <button
        type="button"
        aria-label="Details schließen"
        onClick={onClose}
        className="absolute inset-0 bg-black/45"
      />

      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Alarmdetails"
        className="enter relative flex h-full w-full max-w-[34rem] flex-col border-l border-line bg-raised shadow-[var(--shadow-pop)]"
      >
        <header className="flex items-start justify-between gap-3 border-b border-line px-5 py-4">
          <div className="min-w-0">
            {data ? (
              <>
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <SeverityChip severity={data.severity} />
                  <StatusChip status={data.status} />
                  <Tag>{data.source_key}</Tag>
                </div>
                <h2 className="text-[15px] leading-snug font-semibold text-ink">{data.title}</h2>
              </>
            ) : (
              <div className="skeleton h-5 w-52 rounded-md" />
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Schließen"
            className="-mt-1 rounded-md p-1.5 text-ink-3 transition-colors hover:bg-hover hover:text-ink"
          >
            <XIcon size={18} />
          </button>
        </header>

        <div className="flex-1 space-y-6 overflow-y-auto px-5 py-5">
          {alert.isError ? (
            <ErrorState message="Der Alarm konnte nicht geladen werden." onRetry={() => alert.refetch()} />
          ) : data ? (
            <>
              {data.body ? (
                <p className="text-[13px] leading-relaxed whitespace-pre-wrap text-ink-2">{data.body}</p>
              ) : null}

              {data.url ? (
                <a
                  href={data.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="inline-flex items-center gap-1.5 text-[13px] text-accent hover:underline"
                >
                  Quelle öffnen
                  <ArrowSquareOutIcon size={14} />
                </a>
              ) : null}

              <dl className="grid grid-cols-2 gap-x-6 gap-y-3 border-t border-line pt-4 text-[13px]">
                <Detail term="Zuerst gesehen" value={absoluteTime(data.first_seen_at)} />
                <Detail term="Zuletzt gesehen" value={relativeTime(data.last_seen_at)} />
                <Detail term="Wiederholungen" value={String(data.occurrences)} mono />
                <Detail
                  term="Offen seit"
                  value={duration(data.first_seen_at, data.resolved_at)}
                  mono
                />
                {data.acknowledged_by_name ? (
                  <Detail term="Quittiert von" value={data.acknowledged_by_name} />
                ) : null}
                {data.resolved_at ? (
                  <Detail term="Behoben" value={absoluteTime(data.resolved_at)} />
                ) : null}
              </dl>

              {Object.keys(data.labels).length > 0 ? (
                <div className="border-t border-line pt-4">
                  <h3 className="mb-2 text-[12px] font-medium text-ink-2">Labels</h3>
                  <div className="flex flex-wrap gap-1.5">
                    {Object.entries(data.labels).map(([key, value]) => (
                      <Tag key={key}>
                        {key}={value}
                      </Tag>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="border-t border-line pt-4">
                <h3 className="mb-3 text-[12px] font-medium text-ink-2">Zustellungen</h3>
                {deliveries.isLoading ? (
                  <div className="skeleton h-16 w-full rounded-md" />
                ) : deliveries.data && deliveries.data.items.length > 0 ? (
                  <ul className="space-y-2">
                    {deliveries.data.items.map((delivery) => {
                      const tone =
                        delivery.status === 'delivered'
                          ? 'text-accent'
                          : delivery.status === 'dead'
                            ? 'text-critical'
                            : delivery.status === 'failed'
                              ? 'text-warning'
                              : 'text-ink-3';
                      return (
                        <li
                          key={delivery.id}
                          className="rounded-md border border-line bg-sunken px-3 py-2"
                        >
                          <div className="flex items-center justify-between gap-3">
                            <span className="min-w-0 truncate text-[13px] text-ink">
                              {delivery.channel_label}
                              <span className="text-ink-3"> - {delivery.user_name}</span>
                            </span>
                            <span className={cx('shrink-0 text-[12px] font-medium', tone)}>
                              {DELIVERY_STATUS_LABEL[delivery.status]}
                            </span>
                          </div>
                          <p className="mt-0.5 text-[12px] text-ink-3">
                            {delivery.route_name ? `über ${delivery.route_name}` : 'ohne Regel'}
                            {delivery.attempts > 1 ? ` - ${delivery.attempts} Versuche` : ''}
                          </p>
                          {delivery.last_error ? (
                            <p className="mt-1 font-mono text-[11px] leading-relaxed break-words text-critical">
                              {delivery.last_error}
                            </p>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="text-[13px] text-ink-3">
                    Keine Zustellung angelegt. Es hat keine Weiterleitungsregel gegriffen.
                  </p>
                )}
              </div>
            </>
          ) : (
            <div className="space-y-3">
              <div className="skeleton h-4 w-full rounded-md" />
              <div className="skeleton h-4 w-3/4 rounded-md" />
              <div className="skeleton h-20 w-full rounded-md" />
            </div>
          )}
        </div>

        {mayAct && data && data.status !== 'resolved' ? (
          <footer className="flex gap-2 border-t border-line px-5 py-4">
            {data.status === 'firing' ? (
              <Button
                variant="secondary"
                icon={<BellSlashIcon size={15} />}
                disabled={acknowledge.isPending}
                onClick={() => acknowledge.mutate()}
              >
                Quittieren
              </Button>
            ) : null}
            <Button
              variant="primary"
              icon={<CheckIcon size={15} />}
              disabled={resolve.isPending}
              onClick={() => resolve.mutate()}
            >
              Als behoben markieren
            </Button>
          </footer>
        ) : null}
      </aside>
    </div>
  );
}

function Detail({ term, value, mono }: { term: string; value: string; mono?: boolean }) {
  return (
    <div>
      <dt className="text-[12px] text-ink-3">{term}</dt>
      <dd className={cx('mt-0.5 text-ink', mono && 'tnum')}>{value}</dd>
    </div>
  );
}
